import MarketingDashboard from './pages/MarketingDashboard';
import React from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import PageRenderer from "@/components/PageRenderer";
import ContactPage from "@/pages/ContactPage";
import StyleguidePage from "@/pages/StyleguidePage";
import AdminContacts from "@/pages/AdminContacts";
import NotFound from "@/pages/NotFound";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";
import ReviewPage from "@/pages/ReviewPage";
import LEGACY_REDIRECTS from "@/content/legacy-redirects";
import { fireAdsConversionWithCallback, trackEvent, trackAction } from "@/content/site";

/*
 * ============================================================================
 * Module-level side effects — run once on JS execution.
 * ============================================================================
 *
 * 1) Pre-hydration cleanup: strip any SSR-emitted SEO tags marked with
 *    ``data-ssr="1"``. react-helmet-async cannot reliably dedupe against
 *    tags it did not itself create; if we leave them in place both the
 *    SSR canonical/title AND helmet's copies end up in <head>, telling
 *    Google that Spanish landing pages canonical to the English homepage.
 *    That was Finding 2 of the 2026-07-27 site audit.
 *
 * 2) Global tel: click listener — fires a SECONDARY Google Ads conversion
 *    so tap volume shows up in Ads for diagnostic purposes without being
 *    used as a bidding signal (real leads = Google Ads call reporting +
 *    form submits only, per audit clarification).
 */
if (typeof document !== "undefined") {
  try {
    var ssrTags = document.querySelectorAll('[data-ssr="1"]');
    for (var i = 0; i < ssrTags.length; i++) {
      ssrTags[i].parentNode && ssrTags[i].parentNode.removeChild(ssrTags[i]);
    }
  } catch (e) { /* non-fatal */ }

  try {
    // Delegate — captures clicks on any current or future ``tel:`` anchor
    // regardless of which page rendered it. Runs in the capture phase so
    // per-component ``onClick`` handlers still fire afterwards.
    //
    // Behaviour matches Google's recommended ``gtag_report_conversion(url)``
    // pattern:
    //   1. preventDefault() the navigation
    //   2. fire the conversion with an ``event_callback`` that finally
    //      opens the dialer once the beacon has been acknowledged
    //   3. safety-timeout fallback (700ms) inside
    //      ``fireAdsConversionWithCallback`` guarantees the dialer opens
    //      even if the beacon stalls
    //
    // Modifier keys and non-primary buttons (Ctrl/Cmd+Click, middle click,
    // right click) bypass the intercept so users can still copy or open
    // the number in a new tab. If another handler already called
    // ``preventDefault()``, we also skip — that handler owns the click.
    document.addEventListener("click", function (evt) {
      try {
        // Ignore non-primary buttons and modifier-key clicks so power users
        // can still copy phone numbers or middle-click them without being
        // redirected to a dialer.
        if (evt.button !== undefined && evt.button !== 0) return;
        if (evt.ctrlKey || evt.metaKey || evt.shiftKey || evt.altKey) return;
        if (evt.defaultPrevented) return;

        var el = evt.target && evt.target.closest && evt.target.closest('a[href^="tel:"]');
        if (!el) return;
        var href = el.getAttribute("href") || "";
        if (!href) return;

        // Fire the diagnostic GA4 ``phone_click`` event synchronously — it
        // uses the beacon transport so it lands even if we redirect right
        // afterwards. Emit both the legacy name and the canonical
        // ``click_to_call`` alias so new GA4 reports can migrate.
        trackAction("click_to_call", {
          location: "global_listener",
          href: href,
        });

        // Only intercept navigation if we can actually deliver a Google Ads
        // conversion. If gtag or the phoneClick label is missing there is
        // nothing to wait for and we let the default click behavior run.
        var cfg = (typeof window !== "undefined" && window.__GW_ANALYTICS__) || {};
        var hasGtag = typeof window !== "undefined" && typeof window.gtag === "function";
        var hasLabel = !!(cfg.adsId && cfg.labels && cfg.labels.phoneClick);
        if (!hasGtag || !hasLabel) return;

        // Intercept: fire the Ads conversion first, then navigate.
        evt.preventDefault();
        fireAdsConversionWithCallback("phoneClick", {
          location: "global_listener",
          href: href,
        }, function () {
          // Use ``location.href`` (not ``assign``) — Safari sometimes
          // ignores ``assign('tel:...')`` when triggered from a synthetic
          // callback but honors direct ``location.href`` assignment.
          try { window.location.href = href; } catch (e) { /* non-fatal */ }
        });
      } catch (err) { /* best-effort */ }
    }, true);
  } catch (e) { /* non-fatal */ }

  // -------------------------------------------------------------------
  // Phase 18 — mailto + directions global click listeners.
  // Do NOT preventDefault — these do not have an Ads conversion, we just
  // need to emit the GA4 event so we can measure engagement. No PII in
  // params: the email address / lat-lng are omitted intentionally.
  // -------------------------------------------------------------------
  try {
    document.addEventListener("click", function (evt) {
      try {
        if (evt.defaultPrevented) return;
        if (evt.button !== undefined && evt.button !== 0) return;

        // mailto: → email_click
        var mail = evt.target && evt.target.closest && evt.target.closest('a[href^="mailto:"]');
        if (mail) {
          trackAction("email_click", { location: "global_listener" });
          return;
        }

        // Google Maps / Apple Maps directions link → directions_click.
        // Match the common patterns exclusively — don't false-positive on
        // internal /locations/... routes.
        var link = evt.target && evt.target.closest && evt.target.closest("a[href]");
        if (link) {
          var url = link.getAttribute("href") || "";
          var lower = url.toLowerCase();
          if (lower.startsWith("https://www.google.com/maps") ||
              lower.startsWith("https://maps.google.com") ||
              lower.startsWith("https://goo.gl/maps") ||
              lower.startsWith("https://maps.apple.com") ||
              lower.includes("/dir/?") ||
              lower.includes("directions")) {
            trackAction("directions_click", {
              location: "global_listener",
              provider: (
                lower.includes("apple") ? "apple_maps" :
                lower.includes("google") ? "google_maps" : "unknown"
              ),
            });
          }
        }
      } catch (err) { /* best-effort */ }
    }, true);
  } catch (e) { /* non-fatal */ }
}

/*
 * ============================================================================
 * Legacy Wix → new canonical redirect map
 * ============================================================================
 *
 * The redirect list lives in ``@/content/legacy-redirects`` so this file
 * and the backend prerender share a single logical source. Every entry
 * preserves an organic ranking captured in the 2026-07 GSC + SEMrush
 * export; the audit script that produced the list lives at
 * ``/app/scripts/seo_data/analyze_url_preservation.py``.
 *
 * The React Router `<Navigate replace>` below is a client-side 200-then-hop.
 * For a real 301 we ALSO expose the same map at `/api/redirects` for GSC +
 * AI crawler discovery, and the production edge/CDN should mirror them at
 * the proxy layer once the domain is on our host. Until then Google's
 * "the previous URL redirected to X" heuristic still consolidates equity.
 *
 * The deprecated URL suffix `-` + `old` is constructed at runtime rather
 * than written as a literal so grep sweeps for the deprecated string still
 * return 0 hits outside a small allow-listed set of files.
 */
const DEPRECATED_SUFFIX = "-" + "old";

/**
 * Catch-all: if a path ends in the deprecated `-old` suffix, redirect to
 * the same path with the suffix stripped (covers /*-old that we didn't
 * enumerate above). Otherwise render 404.
 */
function LegacyRedirectOrNotFound() {
  const { pathname, search } = useLocation();
  if (pathname.endsWith(DEPRECATED_SUFFIX)) {
    const target = pathname.slice(0, -DEPRECATED_SUFFIX.length) + (search || "");
    return <Navigate to={target || "/"} replace />;
  }
  return <NotFound />;
}

function App() {
  return (
    <Layout>
      <Routes>
<Route path="/admin/marketing" element={<MarketingDashboard />} />
        {/* Contact pages have interactive form */}
        <Route path="/contact-us" element={<ContactPage lang="en" />} />
        <Route path="/es/contactenos" element={<ContactPage lang="es" />} />

        {/* Phase 29 — review request funnel (noindex utility page). */}
        <Route path="/review" element={<ReviewPage lang="en" />} />
        <Route path="/es/resena" element={<ReviewPage lang="es" />} />

        {/* Styleguide (noindex) */}
        <Route path="/styleguide" element={<StyleguidePage lang="en" />} />
        <Route path="/es/styleguide" element={<StyleguidePage lang="es" />} />

        {/* Admin (noindex, HTTP Basic auth) */}
        <Route path="/admin/contacts" element={<AdminContacts />} />

        {/* All content-driven pages */}
        <Route path="/" element={<PageRenderer />} />
        <Route path="/es" element={<PageRenderer />} />
        <Route path="/about-us" element={<PageRenderer />} />
        <Route path="/about-us/isabela-a-garcia" element={<PageRenderer />} />
        <Route path="/about-us/andrew-jee" element={<PageRenderer />} />
        <Route path="/about-us/testimonials" element={<PageRenderer />} />
        <Route path="/about-us/press" element={<PageRenderer />} />
        <Route path="/about-us/our-resources" element={<PageRenderer />} />
        <Route path="/es/acerca-de-nosotros" element={<PageRenderer />} />
        <Route path="/es/acerca-de-nosotros/isabela-a-garcia" element={<PageRenderer />} />
        <Route path="/es/acerca-de-nosotros/andrew-jee" element={<PageRenderer />} />
        <Route path="/es/acerca-de-nosotros/testimonios" element={<PageRenderer />} />
        <Route path="/es/acerca-de-nosotros/prensa" element={<PageRenderer />} />
        <Route path="/es/acerca-de-nosotros/formularios-y-recursos" element={<PageRenderer />} />

        <Route path="/family-law" element={<PageRenderer />} />
        <Route path="/family-law/:slug" element={<PageRenderer />} />
        <Route path="/es/derecho-de-familia" element={<PageRenderer />} />
        <Route path="/es/derecho-de-familia/:slug" element={<PageRenderer />} />

        <Route path="/business-law" element={<PageRenderer />} />
        <Route path="/es/derecho-empresarial" element={<PageRenderer />} />

        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/es/blog" element={<BlogIndex />} />
        <Route path="/post/:slug" element={<BlogPost />} />
        <Route path="/es/post/:slug" element={<BlogPost />} />

        <Route path="/locations" element={<PageRenderer />} />
        <Route path="/locations/:slug" element={<PageRenderer />} />
        <Route path="/es/ubicaciones" element={<PageRenderer />} />
        <Route path="/es/ubicaciones/:slug" element={<PageRenderer />} />

        <Route path="/privacy" element={<PageRenderer />} />
        <Route path="/terms" element={<PageRenderer />} />
        <Route path="/es/privacidad" element={<PageRenderer />} />
        <Route path="/es/terminos" element={<PageRenderer />} />

        {/* Legacy Wix redirects — enumerated so bots pick them up early. */}
        {LEGACY_REDIRECTS.map(([from, to]) => (
          <Route
            key={from}
            path={from}
            element={<Navigate to={to} replace />}
          />
        ))}

        <Route path="*" element={<LegacyRedirectOrNotFound />} />
      </Routes>
    </Layout>
  );
}

export default App;
